import { useMemo } from "react";
import { Popconfirm, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import ResizableDrawer from "@/components/UI/ResizableDrawer";
import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import { useUser } from "@/contexts/UserContext";
import { TableRow as DetailsRow } from "@/views/DataDiscrepancies/Components";
import { linkRenderer, renderCell } from "@/utils/tableConfigs/GlobalFunctions";
import TasksCommentsSection from "./TasksCommentsSection";
import type { TaskTableRow } from "./utils";

const { Title } = Typography;

type TasksDrawerProps = {
  selectedRecord: TaskTableRow | null;
  setSelectedRecord: (record: TaskTableRow | null) => void;
  onRemove: (task_id: string) => void;
};

export default function TasksDrawer({
  selectedRecord,
  setSelectedRecord,
  onRemove,
}: TasksDrawerProps) {
  const { userInfo } = useUser();
  const currentUserName = useMemo(() => {
    const firstName = userInfo?.first_name?.trim() ?? "";
    const lastName = userInfo?.last_name?.trim() ?? "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName.length > 0 ? fullName : null;
  }, [userInfo?.first_name, userInfo?.last_name]);

  const headersArray = [
    {
      key: "TASK ID",
      value: (
        <span className="text-white">{selectedRecord?.task_id || "-"}</span>
      ),
    },
  ];

  return (
    <ResizableDrawer
      headerTitle="Task Information"
      headersArray={headersArray}
      sizeToggle={{
        collapsedWidth: 654,
        expandedWidth: 1250,
        initialWidth: 654,
      }}
      placement="right"
      open={selectedRecord !== null}
      className="data-management-drawer"
      onClose={() => setSelectedRecord(null)}
      closeIcon={null}
      closable={false}
      headerActions={
        selectedRecord && (
          <Popconfirm
            title="Remove this task?"
            description="This task will be removed from the list."
            okText="Remove"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={() => onRemove(String(selectedRecord.task_id))}
          >
            <button
              type="button"
              aria-label="Remove task"
              className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 leading-none text-white opacity-70 hover:opacity-100"
            >
              <DeleteOutlined className="text-base" />
            </button>
          </Popconfirm>
        )
      }
    >
      {selectedRecord ? (
        <div className="flex flex-col gap-6">
          <section>
            <Title level={5}>Task Definition</Title>
            <div className="rounded-sm bg-white">
              <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <DetailsRow
                    label="Task Title"
                    value={renderCell(selectedRecord.task_title)}
                  />
                  <DetailsRow
                    label="Description"
                    value={renderCell(selectedRecord.description)}
                  />
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <Title level={5}>Linked Context</Title>
            <div className="rounded-sm bg-white">
              <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <DetailsRow
                    label="Issue ID"
                    value={renderCell(
                      selectedRecord.issue_id,
                      linkRenderer((id) => `../issues?openIssue=${id}`),
                    )}
                  />
                  <DetailsRow
                    label="Signal"
                    value={renderCell(selectedRecord.signal)}
                  />
                  <DetailsRow
                    label="Risk"
                    value={renderCell(
                      selectedRecord.risk,
                      linkRenderer((id) => `../risks?openRisk=${id}`),
                    )}
                  />
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <Title level={5}>Ownership and Timeline</Title>
            <div className="rounded-sm bg-white">
              <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <DetailsRow
                    label="Assigned To"
                    value={
                      <AvatarWithInitials
                        name={
                          typeof selectedRecord.assigned_role === "string" &&
                          selectedRecord.assigned_role.trim().length > 0
                            ? selectedRecord.assigned_role
                            : null
                        }
                        avatarSize={28}
                        labelFontSize={14}
                      />
                    }
                  />
                  <DetailsRow
                    label="Priority"
                    value={renderCell(selectedRecord.priority)}
                  />
                  <DetailsRow
                    label="Due Date"
                    value={renderCell(selectedRecord.due_date)}
                  />
                  <DetailsRow
                    label="Status"
                    value={renderCell(selectedRecord.status)}
                  />
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <Title level={5}>Comments and Follow-Up</Title>
            <div className="rounded-sm bg-white">
              <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <DetailsRow
                    label="Comments"
                    value={renderCell(selectedRecord.comments)}
                  />
                </tbody>
              </table>
            </div>
          </section>
          <TasksCommentsSection
            currentUserName={currentUserName}
            taskId={String(selectedRecord.task_id ?? "")}
          />
        </div>
      ) : null}
    </ResizableDrawer>
  );
}
